import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Eye, Send, Trash2, FileText, Download } from 'lucide-react';

interface SessionCardProps {
  session: any;
  isPractice: boolean;
  onSubmit?: () => void;
  onDelete?: () => void;
  onView: () => void;
}

export function SessionCard({ session, isPractice, onSubmit, onDelete, onView }: SessionCardProps) {
  const [showTranscript, setShowTranscript] = useState(false);

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return 'bg-success';
    if (score >= 60) return 'bg-warning';
    return 'bg-destructive';
  };

  const handleDownloadTranscript = () => {
    const agentName = session.simulation_agents?.name || 'Unknown Agent';
    const date = new Date(session.evaluated_at || session.created_at).toLocaleDateString();
    
    const content = `CONVERSATION TRANSCRIPT
========================
Agent: ${agentName}
Date: ${date}
Session ID: ${session.id}

========================
TRANSCRIPT
========================

${session.transcript || 'No transcript available'}`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${agentName.replace(/\s+/g, '-').toLowerCase()}-${session.id.substring(0, 8)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors">
        <div className="flex items-center gap-4">
          {/* Score Circle */}
          <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-lg ${getScoreBgColor(session.overall_score)}`}>
            {session.overall_score}%
          </div>
          
          {/* Details */}
          <div>
            <h4 className="font-medium text-foreground">
              {session.simulation_agents?.name || session.agent_name || 'Unknown Agent'}
            </h4>
            <p className="text-sm text-muted-foreground">
              {isPractice ? 'Practiced' : 'Submitted'}{' '}
              {formatDistanceToNow(
                new Date(isPractice ? session.evaluated_at : session.submitted_at),
                { addSuffix: true }
              )}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={isPractice ? 'secondary' : 'default'}>
                {isPractice ? '🔒 Private' : '✅ Submitted'}
              </Badge>
              {session.simulation_agents?.type && (
                <Badge variant="outline">
                  {session.simulation_agents.type}
                </Badge>
              )}
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onView}>
            <Eye className="w-4 h-4 mr-1" />
            View
          </Button>
          
          {session.transcript && (
            <>
              <Button variant="ghost" size="sm" onClick={() => setShowTranscript(true)}>
                <FileText className="w-4 h-4 mr-1" />
                Transcript
              </Button>
              <Button variant="ghost" size="sm" onClick={handleDownloadTranscript}>
                <Download className="w-4 h-4" />
              </Button>
            </>
          )}
          
          {isPractice && onSubmit && (
            <Button 
              variant="default" 
              size="sm"
              onClick={onSubmit}
              className="bg-success hover:bg-success/90 text-success-foreground"
            >
              <Send className="w-4 h-4 mr-1" />
              Submit
            </Button>
          )}
          
          {isPractice && onDelete && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onDelete}
              className="text-destructive hover:text-destructive/90"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Transcript Dialog */}
      <Dialog open={showTranscript} onOpenChange={setShowTranscript}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Conversation Transcript</DialogTitle>
            <DialogDescription className="sr-only">View the full conversation transcript for this session</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Session with {session.simulation_agents?.name || session.agent_name || 'Unknown Agent'}
            </p>
            <pre className="whitespace-pre-wrap text-sm font-mono bg-muted p-4 rounded-lg">
              {session.transcript || 'No transcript available'}
            </pre>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
